import { ANALYTICS_CONFIG, getGoogleAnalyticsId, getConsentSettings } from './config.js';
import { logger, isGoogleAnalyticsAvailable, withTimeout } from './utils.js';

class GoogleAnalyticsService {
  constructor() {
    this.isInitialized = false;
    this.isLoading = false;
    this.measurementId = getGoogleAnalyticsId();
    this.scriptId = ANALYTICS_CONFIG.GOOGLE_ANALYTICS.SCRIPT_ID;
  }

  // Caricamento script GA4
  async loadScript() {
    if (this.isLoading || this.isInitialized) {
      logger.log('GA4 script already loading or loaded');
      return;
    }

    if (!this.measurementId) {
      logger.error('Google Analytics Measurement ID is missing');
      throw new Error('Google Analytics Measurement ID is missing');
    }

    this.isLoading = true;
    logger.log('Loading Google Analytics script...');

    try {
      await this._loadGtagScript();
      this._initializeGtag();
      this._configureConsentMode();
      this.isInitialized = true;
      this.isLoading = false;
      logger.log('Google Analytics initialized successfully');
    } catch (error) {
      this.isLoading = false;
      logger.error('Failed to load Google Analytics', error);
      throw error;
    }
  }

  // Caricamento script gtag
  _loadGtagScript() {
    return new Promise((resolve, reject) => {
      // Controlla se lo script è già presente
      if (document.getElementById(this.scriptId)) {
        logger.log('GA4 script already exists in DOM');
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = `${ANALYTICS_CONFIG.GOOGLE_ANALYTICS.SCRIPT_URL}?id=${this.measurementId}`;
      script.id = this.scriptId;
      script.async = true;
      
      script.onload = () => {
        logger.log('GA4 script loaded successfully');
        resolve();
      };
      
      script.onerror = (error) => {
        logger.error('Failed to load GA4 script', error);
        reject(new Error('Failed to load Google Analytics script'));
      };

      document.head.appendChild(script);
    });
  }

  // Inizializzazione gtag
  _initializeGtag() {
    if (typeof window === 'undefined') {
      throw new Error('Window object not available');
    }

    // Inizializza dataLayer
    window.dataLayer = window.dataLayer || [];
    
    // Definisci gtag function
    function gtag() {
      window.dataLayer.push(arguments);
    }
    
    gtag('js', new Date());
    window.gtag = gtag;
    
    logger.log('Gtag function initialized');
  }

  // Configurazione modalità consenso
  _configureConsentMode() {
    if (!window.gtag) {
      throw new Error('Gtag not available for consent configuration');
    }

    const consentConfig = {
      page_title: document.title,
      page_location: window.location.href,
      consent_mode: ANALYTICS_CONFIG.GOOGLE_ANALYTICS.CONSENT_MODE,
      ...getConsentSettings()
    };

    window.gtag('config', this.measurementId, consentConfig);
    logger.log('Consent mode configured', consentConfig);
  }

  // Aggiornamento consenso
  updateConsent(consentSettings) {
    if (!isGoogleAnalyticsAvailable()) {
      logger.warn('Google Analytics not available for consent update');
      return;
    }

    try {
      const consentUpdate = {
        ad_storage: consentSettings.marketing ? 'granted' : 'denied',
        analytics_storage: consentSettings.analytics ? 'granted' : 'denied',
        functionality_storage: consentSettings.functionality ? 'granted' : 'denied',
        personalization_storage: consentSettings.personalization ? 'granted' : 'denied',
        security_storage: 'granted'
      };

      window.gtag('consent', 'update', consentUpdate);
      logger.log('Consent updated successfully', consentUpdate);

      // Non inviare pageview automaticamente - sarà gestito dal hook useAnalytics
    } catch (error) {
      logger.error('Failed to update consent', error);
    }
  }

  // Invio pageview (metodo pubblico)
  sendPageView() {
    if (!isGoogleAnalyticsAvailable()) {
      return;
    }

    try {
      withTimeout(
        new Promise((resolve) => {
          window.gtag('event', ANALYTICS_CONFIG.EVENTS.PAGE_VIEW, {
            page_title: document.title,
            page_location: window.location.href,
            consent_granted: true
          });
          resolve();
        })
      ).then(() => {
        logger.log('Pageview sent successfully');
      }).catch((error) => {
        logger.error('Failed to send pageview', error);
      });
    } catch (error) {
      logger.error('Error sending pageview', error);
    }
  }

  // Invio pageview (metodo privato per uso interno)
  _sendPageView() {
    this.sendPageView();
  }

  // Invio evento personalizzato
  sendEvent(eventName, parameters = {}) {
    if (!isGoogleAnalyticsAvailable()) {
      logger.warn('Google Analytics not available for event tracking');
      return;
    }

    try {
      const eventData = {
        ...parameters,
        timestamp: new Date().toISOString()
      };

      window.gtag('event', eventName, eventData);
      logger.log(`Event sent: ${eventName}`, eventData);
    } catch (error) {
      logger.error(`Failed to send event: ${eventName}`, error);
    }
  }

  // Rimozione GA4
  remove() {
    try {
      // Rimuovi script
      const script = document.getElementById(this.scriptId);
      if (script) {
        script.remove();
      }

      // Rimuovi oggetti globali
      if (window.gtag) {
        delete window.gtag;
      }
      if (window.dataLayer) {
        delete window.dataLayer;
      }

      this.isInitialized = false;
      this.isLoading = false;
      logger.log('Google Analytics removed successfully');
    } catch (error) {
      logger.error('Failed to remove Google Analytics', error);
    }
  }

  // Controllo stato
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      isLoading: this.isLoading,
      isAvailable: isGoogleAnalyticsAvailable(),
      measurementId: this.measurementId
    };
  }
}

// Singleton instance
const googleAnalyticsService = new GoogleAnalyticsService();

export default googleAnalyticsService; 